import { FC, useEffect, useState } from "react";
import config from "../../config";
import fetch from "../../utils/fetch";
import TranscriptionQa from "./TranscriptionQa";
import { fetchArticle } from "src/actions/article";
import { ILineBreak, IPronSelection, ISelection, ITranscriptExtended } from "./types";
import { exportCsvReport, formatTime, isAiGenerationType, isTranscriptFailed, isTranscriptPending, jsonToCsv, mergeDiffItemsWithByGroupId, truncateTheWordsText } from "./utils";
import { useParams } from "react-router-dom";
import { connect } from "react-redux";
import IArticle from "src/types/article";
import { Dispatch, bindActionCreators } from "@reduxjs/toolkit";
import { IState } from "src/reducers";
import PronunciationsQa from "./PronunciationsQa";
import { fetchPronunciations } from "src/actions/pronunciation";
import QaCompleted from "./QaCompleted";
import toast from "react-hot-toast";

interface IPropsFromState {
    isLoading: boolean;
    article?: IArticle;
}

interface IPropsFromDispatch {
    fetchArticle: typeof fetchArticle;
    fetchPronunciations: typeof fetchPronunciations;
}

interface IProps {
    setPageTitle: (title: string) => void;
}

const QaMain: FC<IProps & IPropsFromState & IPropsFromDispatch> = ({ article, isLoading, fetchArticle: fetchArticleFn, fetchPronunciations: fetchPronunciationsFn, setPageTitle }) => {
    const [qaType, setQaType] = useState<"TRANSCRIPTION" | "PRONUNCIATIONS" | "COMPLETED">("TRANSCRIPTION");
    const [transcript, setTranscript] = useState<ITranscriptExtended>();
    const [transcriptionSelections, setTranscriptionSelections] = useState<ISelection[]>([]);
    const [pronunciationsSelections, setPronunciationsSelections] = useState<IPronSelection[]>([]);
    const [isTranscriptLoading, setIsTranscriptLoading] = useState(false);

    const [lineBreaks, setLineBreaks] = useState<ILineBreak[]>([]);

    const { id: articleId } = useParams<{ id: string }>();

    const downloadReport = () => {
        const merged = mergeDiffItemsWithByGroupId<ISelection>(transcriptionSelections.filter((item) => item.selectionType === "PICKUP"));

        const transcriptSelectionsTransformed = merged.map((item) => ({
            type: item.type,
            timestamp: item.startTime ? formatTime(item.startTime * 1000) : "--",
            "original text": item.reference && truncateTheWordsText(item.type === "LINEBREAK" ? `After "${item.reference}"` : item.reference, 50),
            "transcripted text": item.hypothesis && truncateTheWordsText(item.hypothesis, 50),
            word: "",
            phonetics: "",
            paragraph: item.line,
        }));
        const pronsSelectionsTransformed = pronunciationsSelections
            .filter((item) => item.selectionType === "PICKUP")
            .map((item) => ({
                type: "PRONUNCIATION",
                timestamp: item.diffItems?.[0]?.startTime ? formatTime(item.diffItems[0].startTime * 1000) : "--",
                "original text": "",
                "transcripted text": "",
                word: item.word,
                phonetics: isAiGenerationType(article) ? item.pron.aiPhonetic || "" : item.pron.pronunciationComment || "",
                paragraph: item.line,
            }));

        const pickups = [...transcriptSelectionsTransformed, ...pronsSelectionsTransformed];

        if (!pickups.length) {
            // it means all the issues were ignored
            return;
        }

        const csvText = jsonToCsv(pickups);

        const title = `NOA-QA-A${articleId}-T${new Date().getTime()}`;
        exportCsvReport(title, csvText);
    };

    const goToPronunciationsQa = () => setQaType("PRONUNCIATIONS");
    const goToCompletedQa = () => setQaType("COMPLETED");

    const saveTranscriptionSelections = (selections: ISelection[]) => {
        setTranscriptionSelections(selections);
        goToPronunciationsQa();
    };

    const savePronunciationsSelections = (selections: IPronSelection[]) => {
        setPronunciationsSelections(selections);
        goToCompletedQa();
    };

    useEffect(() => {
        fetchArticleFn(+articleId);

        document.body.style.overflow = "hidden";

        return () => {
            document.body.style.overflow = "auto";
        };
    }, []);

    useEffect(() => {
        setIsTranscriptLoading(true);

        fetch(`${config.api.url}${config.api.paths.article}/${articleId}/qa-transcript`)
            .then((response) => response.json())
            .then((response) => {
                if (!response?.data?.lineBreakDiffItems?.length || !response?.data?.transcript) {
                    throw new Error("Transcript or line break diff items are not available");
                }

                const { lineBreakDiffItems, transcript } = response?.data;

                setLineBreaks(lineBreakDiffItems);
                setTranscript(transcript);
            })
            .then(() => {
                fetchPronunciationsFn({ limit: 1000000000000 });
            })
            .catch((error) => {
                console.log(error);
                toast.error("Failed to fetch transcript");
            })
            .finally(() => {
                setIsTranscriptLoading(false);
            });
    }, []);

    useEffect(() => {
        if (article?.articleID) {
            const title = `QA | ${article && article.articleID > 0 ? article?.articleName : ""}`;
            setPageTitle(title);
        }
    }, [article?.articleID]);

    const isFailed = isTranscriptFailed(article?.transcriptGenerationStatus);
    const isPending = isTranscriptPending(article?.transcriptGenerationStatus);

    if (isLoading || isTranscriptLoading) return <h4 style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center" }}>Loading...</h4>;
    if (isFailed) return <h4 style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center" }}>Transcription Failed.</h4>;
    if (isPending)
        return <h4 style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center" }}>Transcript is being generated. Please check in few mins.</h4>;
    if (!transcript || !lineBreaks) return null;

    if (qaType === "TRANSCRIPTION") return <TranscriptionQa transcript={transcript} article={article} lineBreaks={lineBreaks} saveTranscriptionSelections={saveTranscriptionSelections} />;

    if (qaType === "PRONUNCIATIONS")
        return <PronunciationsQa transcript={transcript} article={article} savePronunciationsSelections={savePronunciationsSelections} defaultSelections={pronunciationsSelections} />;

    if (qaType === "COMPLETED") return <QaCompleted pronunciationsSelections={pronunciationsSelections} transcriptionSelections={transcriptionSelections} downloadReport={downloadReport} />;

    return null;
};

function mapStateToProps(state: IState): IPropsFromState {
    return {
        article: state.article.article,
        isLoading: state.article.isLoading,
    };
}

function mapDispatchToProps(dispatch: Dispatch): IPropsFromDispatch {
    return bindActionCreators(
        {
            fetchArticle,
            fetchPronunciations,
        },
        dispatch,
    );
}

export default connect(mapStateToProps, mapDispatchToProps)(QaMain);
